import { useEffect, useState, useCallback } from "react";
import { Star, Quote, Globe, ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "motion/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/autoplay";

const PLACE_ID = "ChIJ_-AuePP7ST4Rb7k4EZfXRWA";
const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

interface Review {
  authorAttribution: {
    displayName: string;
    photoUri: string;
    uri: string;
  };
  rating: number;
  text: { text: string; languageCode: string };
  relativePublishTimeDescription: string;
}

interface PlaceData {
  rating: number;
  userRatingCount: number;
  reviews: Review[];
  displayName: { text: string };
}

// ── Star Rating ──────────────────────────────────────────────────────────────
function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={
            star <= rating
              ? "w-4 h-4 text-[#c3a565] fill-[#c3a565]"
              : "w-4 h-4 text-[#c3a565]/20 fill-[#c3a565]/20"
          }
          strokeWidth={0}
        />
      ))}
    </div>
  );
}

// ── Google Avatar ─────────────────────────────────────────────────────────────
function GoogleAvatar({
  photoUri,
  displayName,
}: {
  photoUri: string;
  displayName: string;
}) {
  const [failed, setFailed] = useState(false);

  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  if (failed || !photoUri) {
    return (
      <div
        className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center
                   text-white text-sm font-semibold border border-[#c3a565]/15"
        style={{ background: "#c3a565" }}
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      src={photoUri}
      alt={displayName}
      referrerPolicy="no-referrer"
      className="w-10 h-10 rounded-full object-cover shrink-0 border border-[#c3a565]/15"
      onError={() => setFailed(true)}
    />
  );
}

// ── Google Rating Badge ──────────────────────────────────────────────────────
function ReviewBadge({ data }: { data: PlaceData }) {
  return (
    <a
      href={`https://www.google.com/maps/place/?q=place_id:${PLACE_ID}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-4 bg-white border border-[#c3a565]/20
                 rounded-2xl px-5 py-3.5 hover:border-[#c3a565]/50
                 hover:shadow-[0_8px_30px_rgba(195,165,101,0.12)]
                 transition-all duration-300 mx-auto"
    >
      {/* Google "G" logo */}
      <svg width="28" height="28" viewBox="0 0 48 48" className="shrink-0">
        <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.2l6.8-6.8C35.8 2.2 30.2 0 24 0 14.6 0 6.6 5.4 2.6 13.3l7.9 6.1C12.5 13 17.8 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.4c-.5 2.8-2.1 5.2-4.5 6.8l7 5.4C43.1 36.8 46.1 31 46.1 24.5z"/>
        <path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.9-6.1A23.9 23.9 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z"/>
        <path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7-5.4c-2 1.4-4.6 2.2-8.2 2.2-6.2 0-11.5-4.2-13.4-9.8l-7.9 6.1C6.6 42.6 14.6 48 24 48z"/>
      </svg>

      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase font-semibold mb-0.5" style={{color:'#C3A565'}}>
          Google Rating
        </p>
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-2xl font-bold text-[#2b2b2a]">
            {data.rating.toFixed(1)}
          </span>
          <StarRating rating={Math.round(data.rating)} />
        </div>
        <p className="text-[11px]" style={{ fontWeight: 300, color: "#C3A565" }}>
          {data.userRatingCount.toLocaleString()} verified reviews
        </p>
      </div>
    </a>
  );
}

// ── Review Card ──────────────────────────────────────────────────────────────
function ReviewCard({ review }: { review: Review }) {
  const [expanded, setExpanded] = useState(false);
  const text = review.text?.text || "";
  const isLong = text.length > 200;

  return (
    <div
      className="bg-white border border-[#c3a565]/20 rounded-2xl p-6 sm:p-7
                 h-full flex flex-col transition-all duration-500
                 hover:border-[#c3a565]/60
                 hover:shadow-[0_10px_40px_rgba(195,165,101,0.12)]"
    >
      {/* Quote icon */}
      <div className="mb-4">
        <Quote className="w-10 h-10 text-[#c3a565]/30" strokeWidth={1.5} />
      </div>

      {/* Review text */}
      {text && (
        <p
          className="text-[#2b2b2a]/80 leading-relaxed mb-6 flex-1 text-sm"
          style={{ fontWeight: 300 }}
        >
          {isLong && !expanded ? text.slice(0, 200) + "…" : text}
          {isLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="ml-1 text-[#c3a565] hover:text-[#a8894f] text-xs font-medium transition-colors cursor-pointer"
            >
              {expanded ? "Show less" : "Read more"}
            </button>
          )}
        </p>
      )}

      {/* Author */}
      <div className="pt-5 border-t border-[#c3a565]/10 flex items-start gap-3">
        <GoogleAvatar
          photoUri={review.authorAttribution.photoUri}
          displayName={review.authorAttribution.displayName}
        />
        <div>
          <p className="text-[#2b2b2a] text-sm mb-0.5" style={{ fontWeight: 500 }}>
            {review.authorAttribution.displayName}
          </p>
          <p className="text-xs mb-2" style={{ fontWeight: 300, color: "#C3A565" }}>
            {review.relativePublishTimeDescription}
          </p>
          <StarRating rating={review.rating} />
        </div>
      </div>
    </div>
  );
}

// ── Main Export ──────────────────────────────────────────────────────────────
export default function GoogleReviews() {
  const [data, setData] = useState<PlaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewsSwiper, setReviewsSwiper] = useState<any>(null);

  const scrollPrev = useCallback(() => {
    if (reviewsSwiper) reviewsSwiper.slidePrev();
  }, [reviewsSwiper]);

  const scrollNext = useCallback(() => {
    if (reviewsSwiper) reviewsSwiper.slideNext();
  }, [reviewsSwiper]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const fields = "rating,userRatingCount,reviews,displayName";
        const url = `https://places.googleapis.com/v1/places/${PLACE_ID}?fields=${fields}&key=${API_KEY}&languageCode=en`;
        const res = await fetch(url, {
          headers: { "X-Goog-FieldMask": fields },
        });
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        const json = await res.json();
        setData(json);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  if (loading)
    return (
      <section
        className="relative pt-20 pb-5 overflow-hidden w-full"
        style={{ background: "#faf9f7" }}
      >
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="w-8 h-8 rounded-full border-2 border-[#c3a565]/20 border-t-[#c3a565] animate-spin" />
        </div>
      </section>
    );

  if (error)
    return (
      <section
        className="relative py-20 sm:py-24 lg:py-28 overflow-hidden w-full"
        style={{ background: "#faf9f7" }}
      >
        <p className="text-center text-[#2b2b2a]/40 py-6 text-sm">
          Could not load reviews. {error}
        </p>
      </section>
    );

  if (!data) return null;

  const displayReviews =
    (data.reviews?.filter((r) => r.rating === 5).length > 0
      ? data.reviews.filter((r) => r.rating === 5)
      : data.reviews
    )?.slice(0, 80) ?? [];

  return (
    <section
      className="relative py-20 sm:py-24 lg:py-28 overflow-hidden w-full"
      style={{ background: "#faf9f7" }}
    >
      <style>{`
        .google-reviews-swiper .swiper-wrapper {
          align-items: stretch !important;
        }
        .google-reviews-swiper .swiper-slide {
          height: auto !important;
          display: flex !important;
        }
        @media (max-width: 640px) {
          .google-reviews-swiper {
            padding: 0.5rem 0.5rem !important;
          }
        }
      `}</style>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">

        {/* ── Section Header ── */}
        <div className="mb-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex items-center justify-center gap-4 mb-4"
          >
            <div className="h-px w-16 bg-[#c3a565]" />
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-[#c3a565]" strokeWidth={2} fill="#c3a565" />
              <span
                className="text-xs tracking-[0.3em] uppercase text-[#c3a565]"
                style={{ fontWeight: 600 }}
              >
                Client Reviews
              </span>
            </div>
            <div className="h-px w-16 bg-[#c3a565]" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="text-[#2b2b2a] text-3xl sm:text-4xl lg:text-5xl mb-4"
            style={{ fontWeight: 600 }}
          >
            What Our Clients Say
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[#2b2b2a]/60 max-w-2xl mx-auto mb-8"
            style={{ fontWeight: 300, fontSize: "1rem" }}
          >
            Real reviews from Google — verified clients sharing their experience
          </motion.p>

            {/* Rating Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="flex justify-center"
          >
            <ReviewBadge data={data} />
          </motion.div>
        </div>

         {/* ── Swiper Carousel ── */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative mb-12"
        >
          {/* Left Arrow */}
          <button
            onClick={scrollPrev}
            aria-label="Previous review"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center
                       rounded-full border border-[#c3a565]/30 bg-white text-[#c3a565]
                       hover:border-[#c3a565] hover:bg-[#c3a565]/10 transition-all duration-300
                       cursor-pointer shadow-md"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2} />
          </button>

          {/* Right Arrow */}
          <button
            onClick={scrollNext}
            aria-label="Next review"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center
                       rounded-full border border-[#c3a565]/30 bg-white text-[#c3a565]
                       hover:border-[#c3a565] hover:bg-[#c3a565]/10 transition-all duration-300
                       cursor-pointer shadow-md"
          >
            <ChevronRight className="w-5 h-5" strokeWidth={2} />
          </button>

          <Swiper
            onSwiper={setReviewsSwiper}
            modules={[Autoplay]}
            loop={true}
            autoplay={{
              delay: 4500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            spaceBetween={16}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 1.2, spaceBetween: 20 },
              1024: { slidesPerView: 3, spaceBetween: 24 },
            }}
            className="google-reviews-swiper px-12"
          >
            {displayReviews.map((review, i) => (
              <SwiperSlide key={i} className="h-full">
                <ReviewCard review={review} />
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>

       {/* ── CTA ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mt-4"
        >
          <a
            href={`https://www.google.com/maps/place/?q=place_id:${PLACE_ID}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[#c3a565]/40 text-[#c3a565]
                       font-medium px-6 py-3 rounded-xl hover:bg-[#c3a565] hover:text-white
                       transition-all duration-300 text-sm tracking-wide"
          >
            <Globe size={15} />
            See All Reviews on Google
          </a>
        </motion.div>

      </div>
    </section>
  );
}